/**
 * src/App.jsx — routing and the authenticated shell.
 *
 * Two families of route:
 *  - **Protected** (/dashboard, /calendar, …) sit inside <RequireAuth> and the
 *    sidebar layout. They redirect to /login when there is no session.
 *  - **Public** (/book/:slug, /gallery/:slug, /sign/:token) have no chrome and
 *    no session at all — the client following a link from Instagram has no
 *    account and never will.
 *
 * /settings additionally requires the owner role; an artist landing there is
 * sent back to the dashboard rather than shown a page of failing requests.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  BrowserRouter,
  Navigate,
  NavLink,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from 'react-router-dom';

import { AuthProvider, useAuth } from './lib/auth.jsx';
import { api } from './lib/api.js';
import { useApi } from './lib/useApi.js';
import { getTheme } from './lib/theme.js';
import { ErrorBanner } from './components/ui.jsx';
import ThemeSwitcher from './components/ThemeSwitcher.jsx';
import SubscriptionPaymentForm from './components/SubscriptionPaymentForm.jsx';

import Login from './pages/Login.jsx';
import Dashboard from './pages/Dashboard.jsx';
import Calendar from './pages/Calendar.jsx';
import Clients from './pages/Clients.jsx';
import ClientProfile from './pages/ClientProfile.jsx';
import Projects from './pages/Projects.jsx';
import ProjectDetail from './pages/ProjectDetail.jsx';
import Flash from './pages/Flash.jsx';
import ConsentForms from './pages/ConsentForms.jsx';
import Settings from './pages/Settings.jsx';

import BookingPortal from './pages/public/BookingPortal.jsx';
import FlashGallery from './pages/public/FlashGallery.jsx';
import ConsentSign from './pages/public/ConsentSign.jsx';

/** Sidebar entries, in the order an artist works through their day. */
const NAV = [
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/calendar', label: 'Calendar' },
  { to: '/clients', label: 'Clients' },
  { to: '/projects', label: 'Projects' },
  { to: '/flash', label: 'Flash' },
  { to: '/consent', label: 'Consent forms' },
];

/**
 * Gate for authenticated routes.
 *
 * Renders nothing while the session is still being resolved — redirecting
 * during that window would bounce a signed-in user out on every page refresh.
 *
 * @param {{ children: React.ReactNode, ownerOnly?: boolean }} props
 * @returns {JSX.Element}
 */
function RequireAuth({ children, ownerOnly = false }) {
  const { artist, loading, isOwner } = useAuth();
  const location = useLocation();

  if (loading) return <div className="empty">Loading…</div>;
  // `state` carries where they were heading so Login can send them back.
  if (!artist) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (ownerOnly && !isOwner) return <Navigate to="/dashboard" replace />;
  return children;
}

/**
 * Paywall gate for authenticated routes, sitting inside RequireAuth (so it
 * only ever runs for a signed-in artist) and outside Shell (a locked-out
 * studio should not see the sidebar chrome).
 *
 * Fetches GET /api/studio directly rather than going through useAuth(),
 * since subscription status lives on the studio row, not the artist/session
 * payload — see routes/studios.js's publicStudio().
 *
 * @param {{ children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
function RequireSubscription({ children }) {
  const { data, loading, error, reload } = useApi('/api/studio');
  const [params] = useSearchParams();
  const justReturned = params.get('subscribe') === 'return';

  // Returning from Stripe Checkout: the webhook that flips subscription_status
  // to 'active' may not have landed yet, so re-fetch rather than trust the
  // studio object this page mounted with.
  useEffect(() => {
    if (justReturned) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [justReturned]);

  if (loading) return <div className="empty">Loading…</div>;
  if (error) return <ErrorBanner>{error}</ErrorBanner>;

  const studio = data?.studio;
  if (studio && studio.subscriptionStatus !== 'active') {
    return <SubscribeGate studio={studio} justReturned={justReturned} reload={reload} />;
  }
  return children;
}

/**
 * Shown instead of the dashboard for a studio without an active
 * subscription — but the two ways a studio ends up here are treated
 * differently, per policy: paying is part of *setting up* a studio, not a
 * separate step reached after registering.
 *
 *  - `'incomplete'` (never finished setup) — the embedded payment form
 *    (SubscriptionPaymentForm) is fetched and shown automatically, no click
 *    required to reach it. This is the same screen an owner sees whether
 *    they just registered or are returning later having never completed
 *    payment; RequireSubscription is the one gate both paths go through.
 *  - `'past_due'` / `'canceled'` (was paying, stopped) — the only case that
 *    is actually a *block* requiring a deliberate click before the payment
 *    form appears. Resubscribing after a failed payment should never
 *    happen invisibly on page load.
 *
 * Only an owner can actually call `POST /api/studio/subscribe`
 * (owner-only) — an artist sees a message telling them to ask their owner,
 * not a control that would just 403.
 *
 * @param {{ studio: object, justReturned: boolean, reload: () => void }} props
 * @returns {JSX.Element}
 */
function SubscribeGate({ studio, justReturned, reload }) {
  const { isOwner } = useAuth();
  const [checkout, setCheckout] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Guards the auto-fetch effect below against firing twice (a re-render
  // before the request resolves, dev-mode double-invocation).
  const triggered = useRef(false);

  const startCheckout = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post('/api/studio/subscribe');
      setCheckout({ clientSecret: res.clientSecret, publishableKey: res.publishableKey });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }, []);

  const incomplete = studio.subscriptionStatus === 'incomplete';

  useEffect(() => {
    if (incomplete && isOwner && !triggered.current) {
      triggered.current = true;
      startCheckout();
    }
  }, [incomplete, isOwner, startCheckout]);

  const heading = incomplete ? 'Finishing your studio setup' : 'Your subscription needs attention';
  const waitingOnWebhook = confirmed || justReturned;

  // The payment itself is done at this point (either checkout.confirm()
  // resolved, or Stripe redirected back), but subscription_status only
  // flips once the billing webhook (routes/stripe.js) processes it — which
  // can lag by a few seconds. Poll GET /api/studio until it does; the
  // moment reload() picks up 'active', RequireSubscription stops rendering
  // this gate at all and unmounts it, which is what actually ends the
  // polling (not a fixed success condition checked here).
  useEffect(() => {
    if (!waitingOnWebhook) return undefined;
    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      if (attempts > 10) {
        clearInterval(interval);
        return;
      }
      reload();
    }, 2000);
    return () => clearInterval(interval);
  }, [waitingOnWebhook, reload]);

  return (
    <div className="public-page center">
      <div className="card stack" style={{ maxWidth: 420 }}>
        <h1>{heading}</h1>
        {!isOwner ? (
          <p className="muted small">
            Ask your studio owner to {incomplete ? `finish setting up ${studio.name}` : 'update billing'}.
          </p>
        ) : waitingOnWebhook ? (
          <>
            <p className="muted">
              We&rsquo;re confirming your payment with Stripe — this can take a moment. The
              dashboard will unlock automatically once it&rsquo;s through.
            </p>
            <button type="button" className="btn btn-sm" onClick={reload}>
              Check now
            </button>
          </>
        ) : checkout ? (
          <>
            {!incomplete ? (
              <p className="muted small">
                {studio.name}&rsquo;s last payment didn&rsquo;t go through — start a new
                subscription below to keep using the dashboard and taking bookings.
              </p>
            ) : null}
            <SubscriptionPaymentForm
              publishableKey={checkout.publishableKey}
              clientSecret={checkout.clientSecret}
              onSuccess={() => setConfirmed(true)}
            />
          </>
        ) : incomplete ? (
          <>
            <p className="muted">Setting up payment…</p>
            <ErrorBanner>{error}</ErrorBanner>
          </>
        ) : (
          <>
            <p className="muted">
              {studio.name}&rsquo;s last payment didn&rsquo;t go through. Update your billing to
              keep using the dashboard and taking bookings.
            </p>
            <ErrorBanner>{error}</ErrorBanner>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={startCheckout}>
              {busy ? 'Loading…' : 'Update billing'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The sidebar layout wrapping every protected page.
 *
 * @param {{ children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
function Shell({ children }) {
  const { artist, isOwner, logout } = useAuth();
  const [theme, setThemeState] = useState(getTheme);

  // The swatch already applied the theme and saved it to localStorage by the
  // time this fires (see ThemeSwitcher) — this just also saves it to the
  // artist's account, so it follows them to another device. A failure here
  // is not worth surfacing: the theme still applied, it just stays local.
  const handleThemeChange = (next) => {
    setThemeState(next);
    api.patch(`/api/artists/${artist.id}`, { theme: next }).catch(() => {});
  };

  return (
    <div className="shell">
      <nav className="sidebar">
        <div className="sidebar-brand">Studio</div>
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
          >
            {item.label}
          </NavLink>
        ))}
        {isOwner ? (
          <NavLink
            to="/settings"
            className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
          >
            Settings
          </NavLink>
        ) : null}

        <div className="sidebar-footer stack">
          <div className="small">
            <strong>{artist?.name}</strong>
            <div className="muted">{artist?.role}</div>
          </div>
          <ThemeSwitcher value={theme} onChange={handleThemeChange} />
          <button type="button" className="btn btn-sm" onClick={logout}>
            Sign out
          </button>
        </div>
      </nav>
      <main className="main">{children}</main>
    </div>
  );
}

/**
 * Wraps a page in the gate and the shell.
 *
 * @param {{ children: React.ReactNode, ownerOnly?: boolean }} props
 * @returns {JSX.Element}
 */
function Protected({ children, ownerOnly }) {
  return (
    <RequireAuth ownerOnly={ownerOnly}>
      <RequireSubscription>
        <Shell>{children}</Shell>
      </RequireSubscription>
    </RequireAuth>
  );
}

/**
 * Application root.
 *
 * @returns {JSX.Element}
 */
export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public client-facing pages — no chrome, no session. */}
          <Route path="/book/:slug" element={<BookingPortal />} />
          <Route path="/book/:slug/confirmed" element={<BookingPortal />} />
          <Route path="/gallery/:slug" element={<FlashGallery />} />
          <Route path="/sign/:token" element={<ConsentSign />} />

          <Route path="/login" element={<Login />} />

          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/calendar" element={<Protected><Calendar /></Protected>} />
          <Route path="/clients" element={<Protected><Clients /></Protected>} />
          <Route path="/clients/:id" element={<Protected><ClientProfile /></Protected>} />
          <Route path="/projects" element={<Protected><Projects /></Protected>} />
          <Route path="/projects/:id" element={<Protected><ProjectDetail /></Protected>} />
          <Route path="/flash" element={<Protected><Flash /></Protected>} />
          <Route path="/consent" element={<Protected><ConsentForms /></Protected>} />
          <Route path="/settings" element={<Protected ownerOnly><Settings /></Protected>} />

          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="*"
            element={
              <div className="public-page center">
                <h1>Page not found</h1>
                <p className="muted">That link does not go anywhere.</p>
              </div>
            }
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
